// src/components/Blocks/JuiziEventMetaData/View.jsx
import React, { useEffect, useState } from 'react';
import { expandRecurrences } from './recurrenceHelpers'; // adjust path if helper is elsewhere

// Format like: "Tuesday, Sep 02, 2025" or "Tuesday, Sep 02, 2025 - Wednesday, Sep 03, 2025"
function formatDateRange(startISO, endISO, language = 'en') {
  if (!startISO) return null;
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : null;

  const formatDate = (date) => {
    const partsFmt = new Intl.DateTimeFormat(language, {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).formatToParts(date);

    // Build: Weekday, DD Mon YYYY
    const weekday = partsFmt.find((p) => p.type === 'weekday')?.value || '';
    const day = partsFmt.find((p) => p.type === 'day')?.value || '';
    const month = partsFmt.find((p) => p.type === 'month')?.value || '';
    const year = partsFmt.find((p) => p.type === 'year')?.value || '';

    return `${weekday}, ${day} ${month} ${year}`;
  };

  const startDatePart = formatDate(start);
  
  // If no end date or same day, just return start date
  if (!end || start.toDateString() === end.toDateString()) {
    return startDatePart;
  }
  
  // Different days, show range
  const endDatePart = formatDate(end);
  return `${startDatePart} - ${endDatePart}`;
}

export default function JuiziEventMetaData({ properties }) {
  const lang = properties?.language?.token || 'en';

  // Main event fields
  const start = properties?.start || properties?.start_date || properties?.startDate || null;
  const end = properties?.end || properties?.end_date || properties?.endDate || null;
  const openEnd = !!properties?.open_end;

  const contactName =
    properties?.contact_name || properties?.contact_person || properties?.contact || null;
  const contactEmail = properties?.contact_email || properties?.email || null;
  const contactPhone = properties?.contact_phone || properties?.phone || null;
  const location =
    properties?.location?.title || properties?.location || properties?.place || null;

  // Recurrences (expanded via helper)
  const [recurrenceOccs, setRecurrenceOccs] = useState([]);

  useEffect(() => {
    let alive = true;
    expandRecurrences([properties]).then((expanded) => {
      if (!alive || !Array.isArray(expanded)) return;

      const baseStart = start ? new Date(start).getTime() : null;
      const baseEnd = end ? new Date(end).getTime() : null;

      // Collect start—end pairs
      const pairs = expanded
        .map((e) => ({ start: e.start, end: e.end }))
        .filter((o) => !!o.start);

      // Exclude the base occurrence
      const filtered = pairs.filter((o) => {
        const s = new Date(o.start).getTime();
        const e = o.end ? new Date(o.end).getTime() : null;
        const isBase =
          baseStart !== null &&
          s === baseStart &&
          ((baseEnd === null && e === null) || (baseEnd !== null && e === baseEnd));
        return !isBase;
      });

      // Deduplicate by start—end
      const seen = new Set();
      const uniq = filtered.filter((o) => {
        const key = `${o.start}|${o.end || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Sort by start time
      uniq.sort((a, b) => new Date(a.start) - new Date(b.start));

      setRecurrenceOccs(uniq);
    });
    return () => {
      alive = false;
    };
  }, [properties, start, end]);

  return (
    <section className="juizi-event-meta" aria-labelledby="juizi-event-meta-title">

      <div className="juizi-event-meta-wrapper">

        {/* Contact */}
        {(contactName || contactEmail || contactPhone) && (
          <div className="juizi-event-row juizi-event-contact">
            <h3 className="juizi-event-label">Presented by</h3>
            <div className="juizi-event-value">
              {contactName && <div className="contact-name">{contactName}</div>}
              {contactEmail && (
                <div className="contact-email">
                  <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                </div>
              )}
              {contactPhone && <div className="contact-phone">{contactPhone}</div>}
            </div>
          </div>
        )}
        
        <div className="juizi-event-row juizi-event-dates-times">
          <h3 className="juizi-event-label">When</h3>
          {/* Primary When */}
          {start && (
            <p className="juizi-event-when">
              <span className="juizi-event-value" suppressHydrationWarning>
                {openEnd ? formatDateRange(start, null, lang) : formatDateRange(start, end, lang)}
              </span>
            </p>
          )}

          {/* Recurrences */}
          {recurrenceOccs.length > 0 && (
            <div className="juizi-event-recurrences">
              {/* <div className="juizi-event-label">Also on</div>
              <ul className="juizi-event-value"> */}
                {recurrenceOccs.map((o, i) => (
                  <p key={i} suppressHydrationWarning>
                    {formatDateRange(o.start, o.end, lang)}
                  </p>
                ))}
            </div>
          )}
        </div>

        {/* Location */}
        {location && (
          <div className="juizi-event-row juizi-event-location">
            <h3 className="juizi-event-label">Location</h3>
            <div className="juizi-event-value">{location}</div>
          </div>
        )}

      </div>

    </section>
  );
}